/**
 * DoctorSetup
 *
 * First-time onboarding for doctors. Collects specialty, license, experience, and fee,
 * then inserts into user_roles + doctor_profiles + doctor_presence.
 *
 * Accessible at /doctor-setup after login. Redirects to /consultation when done.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Stethoscope, CheckCircle2, Loader2 } from 'lucide-react';

const SPECIALIZATIONS = [
  'General Physician',
  'Cardiologist',
  'Dermatologist',
  'Endocrinologist',
  'Gastroenterologist',
  'Neurologist',
  'Obstetrician / Gynecologist',
  'Ophthalmologist',
  'Orthopedic Surgeon',
  'Pediatrician',
  'Psychiatrist',
  'Pulmonologist',
  'Urologist',
];

export default function DoctorSetup() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<'form' | 'done'>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    fullName: '',
    specialization: '',
    licenseNumber: '',
    yearsExperience: '',
    feeInr: '',
    clinicName: '',
  });

  function set(field: keyof typeof form, value: string) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;

    const { fullName, specialization, licenseNumber, yearsExperience, feeInr, clinicName } = form;
    if (!fullName || !specialization || !licenseNumber || !yearsExperience || !feeInr) {
      setError('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // 1. Update profile full_name
      await (supabase as any)
        .from('profiles')
        .upsert({ id: user.id, full_name: fullName }, { onConflict: 'id' });

      // 2. Insert doctor role
      const { error: roleError } = await (supabase as any)
        .from('user_roles')
        .upsert({ user_id: user.id, role: 'doctor' }, { onConflict: 'user_id,role', ignoreDuplicates: true });
      if (roleError) throw new Error('Could not assign doctor role: ' + roleError.message);

      // 3. Upsert doctor_profiles
      const { error: profileError } = await (supabase as any)
        .from('doctor_profiles')
        .upsert(
          {
            id: user.id,
            specializations: [specialization],
            license_number: licenseNumber,
            years_of_experience: Number(yearsExperience),
            consultation_fee_inr: Number(feeInr),
            clinic_name: clinicName || null,
            availability_status: 'online',
            average_rating: 0,
            total_consultations: 0,
          },
          { onConflict: 'id' }
        );
      if (profileError) throw new Error('Could not save doctor profile: ' + profileError.message);

      // 4. Upsert doctor_presence (start online)
      await (supabase as any)
        .from('doctor_presence')
        .upsert(
          {
            doctor_id: user.id,
            status: 'online',
            last_seen_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'doctor_id' }
        );

      setStep('done');
    } catch (err: any) {
      setError(err.message ?? 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (step === 'done') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center space-y-4">
          <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto" />
          <h1 className="text-2xl font-bold text-foreground">You're all set!</h1>
          <p className="text-muted-foreground">
            Your doctor profile is live. You can now accept patients through the Doctor Portal.
          </p>
          <p className="text-sm text-muted-foreground bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
            <strong>Note:</strong> You may need to sign out and sign back in for the Doctor Portal to appear in the navigation.
          </p>
          <Button className="w-full" size="lg" onClick={() => navigate('/consultation')}>
            Go to Doctor Portal
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <div className="max-w-lg w-full space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <Stethoscope className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Set Up Your Doctor Profile</h1>
          <p className="text-muted-foreground text-sm">
            Complete your profile to start accepting patient consultations on ApneDoctors.
          </p>
        </div>

        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Full name */}
            <div className="space-y-1.5">
              <Label htmlFor="fullName">Full Name <span className="text-red-500">*</span></Label>
              <Input
                id="fullName"
                placeholder="Dr. Anika Sharma"
                value={form.fullName}
                onChange={e => set('fullName', e.target.value)}
                required
              />
            </div>

            {/* Specialization */}
            <div className="space-y-1.5">
              <Label>Specialization <span className="text-red-500">*</span></Label>
              <Select value={form.specialization} onValueChange={v => set('specialization', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select specialization" />
                </SelectTrigger>
                <SelectContent>
                  {SPECIALIZATIONS.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* License */}
            <div className="space-y-1.5">
              <Label htmlFor="licenseNumber">Medical License Number <span className="text-red-500">*</span></Label>
              <Input
                id="licenseNumber"
                placeholder="MCI-XXXX-XXXX"
                value={form.licenseNumber}
                onChange={e => set('licenseNumber', e.target.value)}
                required
              />
            </div>

            {/* Experience + Fee */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="yearsExp">Years of Experience <span className="text-red-500">*</span></Label>
                <Input
                  id="yearsExp"
                  type="number"
                  min="0"
                  max="60"
                  placeholder="5"
                  value={form.yearsExperience}
                  onChange={e => set('yearsExperience', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fee">Consultation Fee (₹) <span className="text-red-500">*</span></Label>
                <Input
                  id="fee"
                  type="number"
                  min="0"
                  placeholder="500"
                  value={form.feeInr}
                  onChange={e => set('feeInr', e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Clinic (optional) */}
            <div className="space-y-1.5">
              <Label htmlFor="clinic">Clinic / Hospital Name <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                id="clinic"
                placeholder="Apollo Hospitals, Delhi"
                value={form.clinicName}
                onChange={e => set('clinicName', e.target.value)}
              />
            </div>

            {error && (
              <p className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Setting up profile…
                </>
              ) : (
                'Complete Setup'
              )}
            </Button>
          </form>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          Already set up? <button className="text-primary underline" onClick={() => navigate('/consultation')}>Go to portal</button>
        </p>
      </div>
    </div>
  );
}
